import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "../common/Footer";
import http from "../../lib/http";
import { toast } from "react-toastify";
import "../../styles/MyOrders.css";

const toCurrency = (n) => `$${Number(n || 0).toFixed(2)}`;

/* ─── Icons ─────────────────────────────────────────── */
const ShoppingBagIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);
const ChevronDownIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 9L12 15L18 9" />
  </svg>
);
const MapPinIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const PackageIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);
const ReceiptIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);
const TruckIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1" y="3" width="15" height="13" />
    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
    <circle cx="5.5" cy="18.5" r="2.5" />
    <circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
);

/* ─── Status config ──────────────────────────────────── */
const STATUS_CONFIG = {
  pending: { color: "#d97706", bg: "#fef3c7", border: "#fde68a" },
  processing: { color: "#2563eb", bg: "#dbeafe", border: "#bfdbfe" },
  shipped: { color: "#7c3aed", bg: "#ede9fe", border: "#ddd6fe" },
  delivered: { color: "#059669", bg: "#d1fae5", border: "#a7f3d0" },
  completed: { color: "#059669", bg: "#d1fae5", border: "#a7f3d0" },
  cancelled: { color: "#dc2626", bg: "#fee2e2", border: "#fecaca" },
  canceled: { color: "#dc2626", bg: "#fee2e2", border: "#fecaca" },
  received: { color: "#0891b2", bg: "#cffafe", border: "#a5f3fc" },
  paid: { color: "#059669", bg: "#d1fae5", border: "#a7f3d0" },
};
const getStatusCfg = (s) =>
  STATUS_CONFIG[(s || "").toLowerCase()] || {
    color: "#6b7280",
    bg: "#f3f4f6",
    border: "#e5e7eb",
  };

const formatDate = (v) => {
  if (!v) return "N/A";
  return new Date(v).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getOrderItems = (o) => o?.items || o?.order_items || o?.orderItems || [];
const getOrderPaidFlag = (o) => {
  const ps = (o?.payment_status || "").toLowerCase();
  const os = (o?.status || "").toLowerCase();
  return ps === "paid" || os === "paid";
};
const getOrderTabKey = (o) => {
  const s = (o?.status || "").toLowerCase();
  if (getOrderPaidFlag(o)) return "paid";
  if (s === "received") return "received";
  if (s === "shipped") return "shipped";
  if (s === "delivered" || s === "completed") return "delivered";
  if (s === "cancelled" || s === "canceled") return "cancelled";
  return "all";
};

const TABS = ["all", "paid", "received", "shipped", "delivered", "cancelled"];

/* ════════════════════════════════════════════════════
   MyOrders
════════════════════════════════════════════════════ */
const MyOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [activeTab, setActiveTab] = useState("all");
  const [cancelTarget, setCancelTarget] = useState(null); // order object to confirm
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const res = await http.get("/api/customer/orders");
      setOrders(res?.data?.data || []);
    } catch (e) {
      console.error("Error loading orders:", e);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const toggleOrder = (id) => {
    setExpandedOrders((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  /* ── Cancel ── */
  const handleCancelConfirm = async () => {
    if (!cancelTarget || cancelling) return;
    setCancelling(true);
    try {
      const res = await http.patch(
        `/api/customer/orders/${cancelTarget.id}/cancel`,
      );
      const updated = res?.data?.data;
      setOrders((prev) =>
        prev.map((o) =>
          o.id === cancelTarget.id
            ? { ...o, status: "cancelled", ...(updated || {}) }
            : o,
        ),
      );
      toast.success(
        `Order #${cancelTarget.order_no || cancelTarget.id} cancelled. Refund is pending.`,
      );
      setCancelTarget(null);
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to cancel order";
      toast.error(msg);
    } finally {
      setCancelling(false);
    }
  };

  const filteredOrders = useMemo(() => {
    if (activeTab === "all") return orders;
    return orders.filter((o) => getOrderTabKey(o) === activeTab);
  }, [orders, activeTab]);

  const getTabCount = (tab) =>
    tab === "all"
      ? orders.length
      : orders.filter((o) => getOrderTabKey(o) === tab).length;

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="mo-screen">
        <div className="mo-loading">
          <div className="co-spinner-wrap">
            <span className="ct-spinner mo-spinner" />
          </div>
          <p>Loading your orders…</p>
        </div>
        <Footer />
      </div>
    );
  }

  /* ── Render ── */
  return (
    <div className="mo-screen">
      <div className="mo-body">
        <div className="mo-container">
          {/* Page title */}
          <div className="mo-page-title">
            <ShoppingBagIcon />
            <span>My Orders</span>
          </div>

          {/* Tab bar */}
          <div className="mo-tabs">
            {TABS.map((tab) => (
              <button
                key={tab}
                className={`mo-tab${activeTab === tab ? " mo-tab-active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                <span className="mo-tab-label">{tab}</span>
                <span className="mo-tab-count">{getTabCount(tab)}</span>
              </button>
            ))}
          </div>

          {/* Empty state */}
          {filteredOrders.length === 0 ? (
            <div className="mo-empty">
              <div className="mo-empty-icon">
                <ShoppingBagIcon />
              </div>
              <p className="mo-empty-title">No orders here yet</p>
              <p className="mo-empty-sub">
                {activeTab === "all"
                  ? "You haven't placed any orders yet."
                  : `No orders found in "${activeTab}".`}
              </p>
              <button
                className="checkout-btn mo-start-btn"
                onClick={() => navigate("/")}
              >
                Start Shopping
              </button>
            </div>
          ) : (
            <div className="mo-list">
              {filteredOrders.map((order) => {
                const isExpanded = expandedOrders.has(order.id);
                const items = getOrderItems(order);
                const paid = getOrderPaidFlag(order);
                const badgeText = paid ? "Paid" : order.status || "Pending";
                const statusKey = paid ? "paid" : order.status;
                const cfg = getStatusCfg(statusKey);

                return (
                  <div
                    key={order.id}
                    className={`mo-order${isExpanded ? " mo-order-open" : ""}`}
                  >
                    {/* ── Order summary row ── */}
                    <div
                      className="mo-order-summary"
                      onClick={() => toggleOrder(order.id)}
                    >
                      <div className="mo-order-left">
                        <div className="mo-order-top">
                          <span className="mo-order-no">
                            #{order.order_no || order.id}
                          </span>
                          <span
                            className="mo-status-badge"
                            style={{
                              color: cfg.color,
                              background: cfg.bg,
                              borderColor: cfg.border,
                            }}
                          >
                            {badgeText}
                          </span>
                        </div>
                        <p className="mo-order-meta">
                          {formatDate(order.created_at)}
                          <span className="mo-meta-dot">·</span>
                          {items.length} item{items.length !== 1 ? "s" : ""}
                        </p>
                      </div>

                      <div className="mo-order-right">
                        <span className="mo-order-total">
                          {toCurrency(order.total_amount)}
                        </span>
                        <span
                          className={`mo-chevron${isExpanded ? " mo-chevron-open" : ""}`}
                        >
                          <ChevronDownIcon />
                        </span>
                      </div>
                    </div>

                    {/* ── Expanded details ── */}
                    {isExpanded && (
                      <div className="mo-order-details">
                        <div className="mo-details-grid">
                          {/* Left: items + shipping */}
                          <div className="mo-details-left">
                            {/* Items */}
                            {items.length > 0 && (
                              <div className="mo-section">
                                <div className="mo-section-title">
                                  <PackageIcon /> Items ({items.length})
                                </div>
                                <div className="mo-items">
                                  {items.map((item, idx) => (
                                    <div key={idx} className="mo-item">
                                      <div className="mo-item-img">
                                        <img
                                          src={
                                            item.product?.product_images?.[0] ||
                                            item.product?.image ||
                                            ""
                                          }
                                          alt={item.product?.name || "Product"}
                                          onError={(e) => {
                                            e.target.style.display = "none";
                                          }}
                                        />
                                      </div>
                                      <div className="mo-item-info">
                                        <p className="mo-item-name">
                                          {item.product?.name || "Product"}
                                        </p>
                                        {item.product?.brand && (
                                          <span className="mo-item-attr">
                                            {item.product.brand}
                                          </span>
                                        )}
                                        <div className="mo-item-attrs">
                                          {item.color && (
                                            <span className="mo-item-attr">
                                              🎨 {item.color}
                                            </span>
                                          )}
                                          {item.size && (
                                            <span className="mo-item-attr">
                                              📐 {item.size}
                                            </span>
                                          )}
                                        </div>
                                        <p className="mo-item-unit">
                                          Unit price: {toCurrency(item.price)} ×{" "}
                                          {item.quantity}
                                        </p>
                                      </div>
                                      <div className="mo-item-subtotal">
                                        {toCurrency(
                                          Number(item.price) *
                                            Number(item.quantity),
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Shipping address */}
                            {order.shipping_address && (
                              <div className="mo-section">
                                <div className="mo-section-title">
                                  <MapPinIcon /> Shipping Address
                                </div>
                                <div className="mo-address-card">
                                  {order.shipping_address.customer_name && (
                                    <p className="mo-addr-name">
                                      {order.shipping_address.customer_name}
                                    </p>
                                  )}
                                  <p className="mo-addr-line">
                                    {order.shipping_address.address_line1}
                                    {order.shipping_address.address_line2 &&
                                      `, ${order.shipping_address.address_line2}`}
                                  </p>
                                  {order.shipping_address.street && (
                                    <p className="mo-addr-line">
                                      {order.shipping_address.street}
                                    </p>
                                  )}
                                  <p className="mo-addr-line">
                                    {order.shipping_address.city}
                                    {order.shipping_address.province &&
                                      `, ${order.shipping_address.province}`}
                                    {order.shipping_address.zip_code &&
                                      ` ${order.shipping_address.zip_code}`}
                                  </p>
                                  <p className="mo-addr-line">
                                    {order.shipping_address.country}
                                  </p>
                                  {order.shipping_address.phone && (
                                    <p className="mo-addr-phone">
                                      📞 {order.shipping_address.phone}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Right: order details panel */}
                          <div className="mo-details-right">
                            <div className="mo-info-card">
                              <div className="mo-info-card-header">
                                <ReceiptIcon /> Order Details
                              </div>

                              <div className="mo-info-rows">
                                <div className="mo-info-row">
                                  <span className="mo-info-label">
                                    Order Number
                                  </span>
                                  <span className="mo-info-value">
                                    #{order.order_no || order.id}
                                  </span>
                                </div>

                                <div className="mo-info-row">
                                  <span className="mo-info-label">Date</span>
                                  <span className="mo-info-value">
                                    {formatDate(order.created_at)}
                                  </span>
                                </div>

                                <div className="mo-info-row">
                                  <span className="mo-info-label">Status</span>
                                  <span
                                    className="mo-status-badge"
                                    style={{
                                      color: cfg.color,
                                      background: cfg.bg,
                                      borderColor: cfg.border,
                                    }}
                                  >
                                    {badgeText}
                                  </span>
                                </div>

                                {order.payment_method && (
                                  <div className="mo-info-row">
                                    <span className="mo-info-label">
                                      Payment
                                    </span>
                                    <span className="mo-info-value">
                                      {order.payment_method === "cash"
                                        ? "Cash on Delivery"
                                        : order.payment_method}
                                    </span>
                                  </div>
                                )}

                                {order.payment_status && (
                                  <div className="mo-info-row">
                                    <span className="mo-info-label">
                                      Pay Status
                                    </span>
                                    <span
                                      className="mo-pay-status"
                                      data-paid={
                                        (
                                          order.payment_status || ""
                                        ).toLowerCase() === "paid"
                                      }
                                    >
                                      {order.payment_status}
                                    </span>
                                  </div>
                                )}

                                {order.order_tracking_id && (
                                  <div className="mo-info-row">
                                    <span className="mo-info-label">
                                      Tracking
                                    </span>
                                    <span className="mo-info-value mo-tracking">
                                      {order.order_tracking_id}
                                    </span>
                                  </div>
                                )}

                                {(order.status || "").toLowerCase() ===
                                  "shipped" && (
                                  <div className="mo-info-row">
                                    <span className="mo-info-label">
                                      Track Order
                                    </span>
                                    <a
                                      href="https://www.ag.express/"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="mo-track-link"
                                    >
                                      ag.express ↗
                                    </a>
                                  </div>
                                )}
                              </div>

                              <div className="mo-info-total-row">
                                <span className="mo-total-label">Total</span>
                                <span className="mo-total-amount">
                                  {toCurrency(order.total_amount)}
                                </span>
                              </div>

                              {/* Cancel — paid orders always, pending orders within 24h */}
                              {["paid", "pending"].includes(
                                (order.status || "").toLowerCase(),
                              ) && (
                                <button
                                  className="mo-cancel-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCancelTarget(order);
                                  }}
                                >
                                  Cancel Order
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <Footer />

      {/* ── Cancel confirmation modal ── */}
      {cancelTarget && (
        <div
          className="mo-overlay"
          onClick={() => {
            if (!cancelling) setCancelTarget(null);
          }}
        >
          <div
            className="mo-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mo-confirm-icon">
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h3 className="mo-confirm-title">Cancel Order?</h3>
            <p className="mo-confirm-sub">
              Order <strong>#{cancelTarget.order_no || cancelTarget.id}</strong>{" "}
              will be cancelled and a refund will be initiated.
            </p>
            <div className="mo-confirm-actions">
              <button
                className="mo-confirm-keep"
                onClick={() => setCancelTarget(null)}
                disabled={cancelling}
              >
                Keep Order
              </button>
              <button
                className="mo-confirm-cancel"
                onClick={handleCancelConfirm}
                disabled={cancelling}
              >
                {cancelling ? (
                  <>
                    <span className="mo-btn-spin" /> Cancelling…
                  </>
                ) : (
                  "Yes, Cancel"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOrders;
